import { useGame } from '@/contexts/GameContext';

interface GameOverModalProps {
  score: number;
  timeElapsed: number;
  errors: number;
  gameName: string;
  message?: string;
  onReplay: () => void;
  onQuit: () => void;
  healthTip?: string;
}

export default function GameOverModal({ score, timeElapsed, errors, gameName, message, onReplay, onQuit, healthTip }: GameOverModalProps) {
  const { profile, formatTime } = useGame();
  const bestScore = profile.bestScores[gameName] || 0;
  const isNewBest = score > 0 && score >= bestScore;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm p-4">
      <div className="bg-background-50 rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl animate-in">
        {isNewBest && (
          <div className="text-center mb-4">
            <div className="inline-block bg-amber-100 text-amber-800 px-4 py-1.5 rounded-full text-sm font-semibold mb-2">
              <i className="ri-trophy-fill mr-1.5"></i> Nouveau record !
            </div>
          </div>
        )}

        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <i className={`text-3xl ${score > 0 ? 'ri-emotion-happy-line text-green-600' : 'ri-emotion-sad-line text-gray-500'}`}></i>
          </div>
          <h2 className="text-xl font-bold text-foreground-950 mb-1">
            {message || (score > 0 ? 'Bravo !' : 'Partie terminée')}
          </h2>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-background-100 rounded-xl p-3 text-center">
            <div className="text-lg font-bold text-foreground-950">{score}</div>
            <div className="text-xs text-foreground-600">Score</div>
          </div>
          <div className="bg-background-100 rounded-xl p-3 text-center">
            <div className="text-lg font-mono font-bold text-foreground-950">{formatTime(timeElapsed)}</div>
            <div className="text-xs text-foreground-600">Temps</div>
          </div>
          <div className="bg-background-100 rounded-xl p-3 text-center">
            <div className="text-lg font-bold text-foreground-950">{errors}</div>
            <div className="text-xs text-foreground-600">Erreurs</div>
          </div>
        </div>

        {healthTip && (
          <div className="bg-accent-100/50 border border-accent-200/50 rounded-xl p-4 mb-5">
            <div className="flex items-start gap-2">
              <i className="ri-lightbulb-line text-accent-600 mt-0.5"></i>
              <p className="text-sm text-accent-900 leading-relaxed">{healthTip}</p>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onReplay}
            className="flex-1 py-3 bg-primary-500 text-background-50 rounded-xl font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-2"></i> Rejouer
          </button>
          <button
            onClick={onQuit}
            className="flex-1 py-3 bg-background-100 text-foreground-700 rounded-xl font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-line mr-2"></i> Menu
          </button>
        </div>
      </div>
    </div>
  );
}